import React, { useState, useEffect, useCallback } from "react";
import axios from '../../../api/axios';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box, Paper, Typography, TextField, InputAdornment, Chip, Button,
  Snackbar, Alert, Stack, Pagination, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Tooltip, useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon, Visibility as ViewIcon, Payment as PaymentIcon,
  Clear as ClearIcon, CheckCircle as PaidIcon, Cancel as UnpaidIcon,
  DateRange as DateRangeIcon, ReceiptLong as InvoiceIcon,
} from '@mui/icons-material';
import useData from '../../../Hooks/useData';
import { formatMoney } from '../../../utils/helpers';
import ApplyPaymentModal from './ApplyPaymentModal';
import styles from './Users.module.css';

const STATUS_COLORS = {
  'New': 'default',
  'Partial Payment': 'warning',
  'Payment Received': 'success',
  'Refunded': 'info',
  'Cancelled': 'error',
};

const Finances = () => {
  const { auth } = useData();
  const token = auth?.token;
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    isPaid: '',
    dateFrom: '',
    dateTo: '',
    customerName: '',
    orderId: '',
    referenceNumber: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [pagination, setPagination] = useState({ page: 1, limit: 25, totalCount: 0, totalPages: 0 });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [paymentModal, setPaymentModal] = useState({ open: false, order: null });
  const [showDateRange, setShowDateRange] = useState(false);

  const fetchOrders = useCallback(async (page = 1, limit = 25, activeFilters = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (activeFilters.status) params.append('status', activeFilters.status);
      if (activeFilters.isPaid) params.append('isPaid', activeFilters.isPaid);
      if (activeFilters.dateFrom) params.append('dateFrom', activeFilters.dateFrom);
      if (activeFilters.dateTo) params.append('dateTo', activeFilters.dateTo);
      if ((activeFilters.customerName || '').trim()) params.append('customerName', activeFilters.customerName.trim());
      if ((activeFilters.orderId || '').trim()) params.append('orderId', activeFilters.orderId.trim());
      if ((activeFilters.referenceNumber || '').trim()) params.append('referenceNumber', activeFilters.referenceNumber.trim());

      const result = await axios.get(`/api/orders?${params}`);
      const data = result.data;
      setOrders(data.orders || []);
      setPagination(data.pagination || { page: 1, limit: 25, totalCount: 0, totalPages: 0 });
    } catch (err) {
      setError("Failed to fetch orders. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

 // Single driver for all fetches
useEffect(() => {
  fetchOrders(paginationModel.page + 1, paginationModel.pageSize, filters);
  // NOTE: don't add filters here — searches are driven by handleSearch
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [paginationModel.page, paginationModel.pageSize]);

// Update handleSearch to just update the model, not call fetchOrders directly
const handleSearch = () => {
  setPaginationModel(prev => ({ ...prev, page: 0 }));
  fetchOrders(1, paginationModel.pageSize, filters); // still explicit here since filters changed
};

  const handleClearSearch = () => {
    const resetFilters = {
      status: '',
      isPaid: '',
      dateFrom: '',
      dateTo: '',
      customerName: '',
      orderId: '',
      referenceNumber: '',
    };
    setFilters(resetFilters);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    fetchOrders(1, paginationModel.pageSize, resetFilters);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleFilterChange = (field) => (e) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  // Remove the direct fetchOrders call from handlePaginationModelChange
const handlePaginationModelChange = useCallback((newModel) => {
  setPaginationModel(newModel);
  // don't call fetchOrders here — the useEffect below handles it
}, []);

  const refreshOrders = () => {
    fetchOrders(pagination.page, pagination.limit, filters);
  };

  const hasActiveFilters = Boolean(
    filters.status
    || filters.isPaid
    || filters.dateFrom
    || filters.dateTo
    || filters.customerName.trim()
    || filters.orderId.trim()
    || filters.referenceNumber.trim()
  );

  const handleApplyPaymentClose = (success) => {
    setPaymentModal({ open: false, order: null });
    if (success) {
      setSnackbar({ open: true, message: 'Payment applied successfully', severity: 'success' });
      //refreshOrders();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: "UTC" });
  };

  const formatCurrency = (value) => formatMoney(value);

  // Download the invoice PDF for an order. The invoice-pdf endpoint requires a
  // Bearer token, so we attach it manually (the default axios instance doesn't)
  // and stream the response as a blob to trigger a browser download.
  const handleDownloadInvoice = async (order) => {
    const orderId = order?.OrderID;
    if (!orderId) return;
    try {
      const response = await axios.get(`/api/orders/${orderId}/invoice-pdf`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-order-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'Invoice downloaded', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to download invoice: ${err.response?.status || err.message}`,
        severity: 'error',
      });
    }
  };

  const columns = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      getActions: (params) => [
        <Tooltip key="view" title="View Order" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<ViewIcon />}
              label="View Order"
              onClick={() => navigate(`/admin/finances/${params.row.OrderID}`)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="pay" title="Apply Payment" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<PaymentIcon />}
              label="Apply Payment"
              onClick={() => setPaymentModal({ open: true, order: params.row })}
            />
          </span>
        </Tooltip>,
        <Tooltip key="invoice" title="Download Invoice" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<InvoiceIcon />}
              label="Download Invoice"
              onClick={() => handleDownloadInvoice(params.row)}
            />
          </span>
        </Tooltip>,
      ],
    },
    { field: 'OrderID', headerName: 'Order ID', width: 100 },
    {
      field: 'CustomerName',
      headerName: 'Customer',
      width: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.CustomerName || 'N/A'}
          </Typography>
          {params.row.ChurchName && (
            <Typography variant="caption" color="text.secondary">
              {params.row.ChurchName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'EventName',
      headerName: 'Event Name',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value || ''}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'OrderDate',
      headerName: 'Created',
      width: 130,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'AmountDue',
      headerName: 'Amount Due',
      width: 130,
      renderCell: (params) => {
        const val = Number(params.value) || 0;
        return (
          <Typography
            variant="body2"
            fontWeight={val > 0 ? 'bold' : 'normal'}
            color={val > 0 ? 'error.main' : 'text.primary'}
          >
            {formatCurrency(val)}
          </Typography>
        );
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      width: 160,
      renderCell: (params) => {
        const status = params.value || 'New';
        return (
          <Chip
            label={status}
            color={STATUS_COLORS[status] || 'default'}
            size="small"
            variant="filled"
          />
        );
      },
    },
    {
      field: 'IsPaid',
      headerName: 'Paid',
      width: 90,
      renderCell: (params) => params.value
        ? <Chip icon={<PaidIcon />} label="Yes" color="success" size="small" variant="outlined" />
        : <Chip icon={<UnpaidIcon />} label="No" color="error" size="small" variant="outlined" />,
    },
    {
      field: 'PaidToDate',
      headerName: 'Paid To Date',
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
  ];

  if (isLoading && orders.length === 0) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Orders</h1>
                <p>View and manage all payments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography className={styles.loadingText}>Loading orders...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Orders</h1>
              <p>View and manage all payments</p>
            </div>
          </div>
        </div>
      </div>

      <div className="full-row white">
        <Box className={styles.container}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Paper className={styles.searchPaper} sx={{ p: 2 }}>
            {(() => {
              const fieldSx = {
                '& .MuiInputBase-root': { height: 40, fontSize: '0.875rem', color: 'text.primary' },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.23)' },
              };
              return (
                <>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 1.5,
                    mb: 1.5,
                  }}>
                    <TextField
                      label="Customer Name"
                      placeholder="Name or email"
                      value={filters.customerName}
                      onChange={handleFilterChange('customerName')}
                      onKeyPress={handleSearchKeyPress}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>
                        ),
                        endAdornment: filters.customerName && (
                          <InputAdornment position="end">
                            <ClearIcon sx={{ cursor: 'pointer', fontSize: 16 }} onClick={() => setFilters(prev => ({ ...prev, customerName: '' }))} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ gridColumn: 'span 2', ...fieldSx }}
                    />
                    <TextField
                      label="Order Number"
                      placeholder="Order number"
                      value={filters.orderId}
                      onChange={handleFilterChange('orderId')}
                      onKeyPress={handleSearchKeyPress}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                    <TextField
                      label="Reference Number"
                      placeholder="Reference number"
                      value={filters.referenceNumber}
                      onChange={handleFilterChange('referenceNumber')}
                      onKeyPress={handleSearchKeyPress}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                    <FormControl size="small" sx={fieldSx}>
                      <InputLabel shrink>Status</InputLabel>
                      <Select
                        value={filters.status}
                        label="Status"
                        displayEmpty
                        notched
                        onChange={handleFilterChange('status')}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All Statuses</MenuItem>
                        <MenuItem value="New" sx={{ fontSize: '0.875rem' }}>New</MenuItem>
                        <MenuItem value="Partial Payment" sx={{ fontSize: '0.875rem' }}>Partial Payment</MenuItem>
                        <MenuItem value="Payment Received" sx={{ fontSize: '0.875rem' }}>Payment Received</MenuItem>
                        <MenuItem value="Refunded" sx={{ fontSize: '0.875rem' }}>Refunded</MenuItem>
                        <MenuItem value="Cancelled" sx={{ fontSize: '0.875rem' }}>Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={fieldSx}>
                      <InputLabel shrink>Paid</InputLabel>
                      <Select
                        value={filters.isPaid}
                        label="Paid"
                        displayEmpty
                        notched
                        onChange={handleFilterChange('isPaid')}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All</MenuItem>
                        <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Paid</MenuItem>
                        <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Unpaid</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      variant={showDateRange ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<DateRangeIcon />}
                      onClick={() => {
                        setShowDateRange(prev => {
                          if (prev) setFilters(f => ({ ...f, dateFrom: '', dateTo: '' }));
                          return !prev;
                        });
                      }}
                      sx={{ height: 36, fontSize: '0.8rem', textTransform: 'none', whiteSpace: 'nowrap' }}
                    >
                      Date Range
                    </Button>
                    {showDateRange && (
                      <Box sx={{ display: 'flex', gap: 1.5, flex: '1 1 280px', minWidth: 0 }}>
                        <TextField
                          label="Starting Date"
                          type="date"
                          value={filters.dateFrom}
                          onChange={handleFilterChange('dateFrom')}
                          onKeyPress={handleSearchKeyPress}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                        />
                        <TextField
                          label="Ending Date"
                          type="date"
                          value={filters.dateTo}
                          onChange={handleFilterChange('dateTo')}
                          onKeyPress={handleSearchKeyPress}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                        />
                      </Box>
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      {hasActiveFilters && (
                        <Button variant="outlined" onClick={handleClearSearch} size="small" sx={{ height: 36, fontSize: '0.875rem' }}>
                          Clear All
                        </Button>
                      )}
                      <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} className={styles.searchButton} sx={{ height: 36, fontSize: '0.875rem' }}>
                        Search
                      </Button>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </Paper>

          <Paper className={styles.dataGridPaper}>
            <DataGrid
              rows={orders}
              columns={columns}
              getRowId={(row) => row.OrderID}
              loading={isLoading}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.totalCount}
              autoHeight
              disableRowSelectionOnClick
              hideFooterPagination
              disableColumnFilter
              disableDensitySelector
              slots={{ toolbar: null }}
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No orders found</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search or filter.</Typography>
                  </Box>
                ),
              }}
              className="usersDataGrid"
            />
          </Paper>

          <Paper className={styles.paginationPaper}>
            <Stack className={styles.paginationStack}>
              <Typography variant="body2" color="text.secondary">
                Showing {orders.length > 0 ? ((pagination.page - 1) * pagination.limit) + 1 : 0} to {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of {pagination.totalCount} orders
              </Typography>
              <Stack className={styles.paginationControls}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(_, page) => {
                    setPaginationModel(prev => ({ ...prev, page: page - 1 }));
                    //fetchOrders(page, pagination.limit, filters);
                  }}
                  disabled={isLoading}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={1}
                  className="usersPagination"
                />
              </Stack>
              <Box className={styles.pageSizeSelector}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <TextField
                  select
                  size="small"
                  value={pagination.limit}
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setPaginationModel(prev => ({ ...prev, pageSize: newLimit, page: 0 }));
                    //fetchOrders(1, newLimit, filters);
                  }}
                  SelectProps={{ native: true }}
                  className={`${styles.pageSizeField} pageSizeField`}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </TextField>
                <Typography variant="body2" color="text.secondary">per page</Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </div>

      <ApplyPaymentModal
        open={paymentModal.open}
        order={paymentModal.order}
        onClose={handleApplyPaymentClose}
        token={token}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default Finances;